import * as Sentry from "@sentry/react";
import posthog from "posthog-js";
import type { ConsentCategories } from "./consent/types";

type GtagConsentValue = "granted" | "denied";

interface GtagConsentUpdate {
  ad_storage: GtagConsentValue;
  ad_user_data: GtagConsentValue;
  ad_personalization: GtagConsentValue;
  analytics_storage: GtagConsentValue;
  functionality_storage: GtagConsentValue;
  security_storage: GtagConsentValue;
}

type FbqFn = ((...args: unknown[]) => void) & {
  queue?: unknown[];
  loaded?: boolean;
  version?: string;
  push?: unknown;
  callMethod?: (...args: unknown[]) => void;
};

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    fbq?: FbqFn;
    _fbq?: FbqFn;
  }
}

export const GA4_ID = (import.meta.env.VITE_GA4_ID as string | undefined) ?? "";
export const META_PIXEL_ID =
  (import.meta.env.VITE_META_PIXEL_ID as string | undefined) ?? "";
export const POSTHOG_KEY =
  (import.meta.env.VITE_POSTHOG_KEY as string | undefined) ?? "";
export const POSTHOG_HOST =
  (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ??
  "https://eu.posthog.com";

function ensureGtag(): void {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer ?? [];
  if (!window.gtag) {
    window.gtag = function gtag(...args: unknown[]) {
      window.dataLayer?.push(args);
    };
  }
}

/**
 * Ensures `window.gtag` exists even if the inline head snippet has not yet
 * parsed (defensive). The canonical Consent Mode v2 defaults are now emitted
 * directly from the `<head>` snippet in `index.html` — this function is a
 * safety net that re-emits defaults only when `window.gtag` is missing
 * (e.g. the head snippet was blocked). Safe to call repeatedly.
 */
export function emitDefaultConsent(): void {
  if (typeof window === "undefined") return;
  if (window.gtag) return; // head snippet already handled defaults
  ensureGtag();
  window.gtag?.("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    functionality_storage: "granted",
    security_storage: "granted",
    wait_for_update: 500,
    region: ["GB", "EU"],
  });
}

export function buildConsentUpdate(
  categories: ConsentCategories,
): GtagConsentUpdate {
  const analytics: GtagConsentValue = categories.analytics
    ? "granted"
    : "denied";
  const marketing: GtagConsentValue = categories.marketing
    ? "granted"
    : "denied";
  const functional: GtagConsentValue = categories.functional
    ? "granted"
    : "denied";
  return {
    ad_storage: marketing,
    ad_user_data: marketing,
    ad_personalization: marketing,
    analytics_storage: analytics,
    functionality_storage: functional,
    security_storage: "granted",
  };
}

// Module-level consent latch, mirroring the `posthogLoaded` pattern below:
// lets non-React call sites (e.g. form submit handlers) fire GA4 events
// without reading the ConsentProvider context. Flipped by `updateConsent`,
// which AnalyticsScripts calls on every consent-state change (including the
// stored state on page load). Defaults to denied.
let ga4AnalyticsConsent = false;

export function updateConsent(categories: ConsentCategories): void {
  if (typeof window === "undefined") return;
  ga4AnalyticsConsent = categories.analytics;
  ensureGtag();
  window.gtag?.("consent", "update", buildConsentUpdate(categories));
}

/**
 * Should PostHog be initialised for this consent state?
 *
 * A PURE FUNCTION on purpose, and extracted from AnalyticsScripts deliberately.
 * The decision used to live inline in the component's effect, where the only way
 * to check it was to read the condition's TEXT — and a review proved that cannot
 * work: a single `!` inverted the gate (initialising PostHog for exactly the
 * visitors who DECLINED, with autocapture and session replay on) while a scan
 * matching /categories\.analytics/ stayed green, because the negation matches the
 * same regex as the correct condition.
 *
 * Polarity is a behavioural property, so it needs a behavioural test. Here it has
 * one: conversionParity.test.ts asserts granted -> true, denied -> false, and no
 * key -> false. A guard cannot be simultaneously blind to inversion and strict
 * about wording; moving the decision here removes the need for it to be either.
 */
export function shouldLoadPostHog(
  categories: Pick<ConsentCategories, "analytics">,
): boolean {
  return Boolean(POSTHOG_KEY) && categories.analytics === true;
}

let posthogLoaded = false;

export function loadPostHog(): void {
  if (typeof window === "undefined") return;
  if (!POSTHOG_KEY) return;
  if (posthogLoaded) return;
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    person_profiles: "identified_only",
    capture_pageview: false,
    autocapture: true,
    disable_session_recording: false,
    respect_dnt: true,
  });
  posthogLoaded = true;
}

export function posthogOptIn(): void {
  if (typeof window === "undefined" || !posthogLoaded) return;
  posthog.opt_in_capturing();
}

export function posthogOptOut(): void {
  if (typeof window === "undefined" || !posthogLoaded) return;
  posthog.opt_out_capturing();
}

/**
 * Register (or clear) the `cluster_tag` super-property.
 *
 * Deliberately does NOT capture a pageview. It used to, which coupled all
 * pageview tracking to the cluster classifier: removing the dead classifier
 * would have silently killed every $pageview on the site. Capture is now
 * `capturePageview()` and the two are called explicitly side by side.
 *
 * Nothing supplies a tag today. The LL-0015 path table that used to feed this
 * was deleted on 2026-08-01 (every path in it, /guides/* and
 * /find-flatmates-*, was removed in the Happenings pivot and now 301s, so it
 * returned null for every live route). The replacement is a server-provided
 * cluster on the article routes, which lands with the new taxonomy.
 */
export function setClusterTag(tag: string | null): void {
  if (typeof window === "undefined" || !posthogLoaded) return;
  if (tag) {
    posthog.register({ cluster_tag: tag });
  } else {
    // Super-properties persist in localStorage, so a returning visitor can
    // still be carrying a cluster_tag from the old path table. Clear it.
    posthog.unregister("cluster_tag");
  }
}

/** Emit a $pageview. PostHog is initialised with capture_pageview: false. */
export function capturePageview(): void {
  if (typeof window === "undefined" || !posthogLoaded) return;
  posthog.capture("$pageview");
}

export function captureEvent(
  name: string,
  properties?: Record<string, string | number | boolean>,
): void {
  if (typeof window === "undefined" || !posthogLoaded) return;
  posthog.capture(name, properties);
}

/**
 * Fires a GA4 event via `gtag('event', ...)`, hard-gated on the analytics
 * consent category (LL-0024). Matches the PostHog gating pattern above
 * (`captureEvent` no-ops until consent loads PostHog): no GA4 event is
 * emitted pre-consent, even though gtag itself is Consent-Mode-v2
 * default-denied. The LL-0024 acceptance criterion is that GA4 DebugView
 * shows NO events until the user accepts analytics cookies.
 */
export function captureGa4Event(
  name: string,
  params?: Record<string, string | number | boolean>,
): void {
  if (typeof window === "undefined") return;
  if (!ga4AnalyticsConsent) return;
  ensureGtag();
  window.gtag?.("event", name, params);
}

/**
 * Fires a CONVERSION to both GA4 and PostHog.
 *
 * Every conversion on this site (beta_signup, waitlist_signup,
 * beta_survey_submit, events_explore_app_click) used to call captureGa4Event
 * ONLY. PostHog received just three minor interaction events plus autocapture,
 * so no PostHog funnel could be built for any conversion — the one question the
 * site exists to answer was unanswerable in the tool we use to answer it.
 *
 * Both underlying helpers are independently consent-gated (captureEvent no-ops
 * until consent has loaded PostHog; captureGa4Event no-ops unless
 * ga4AnalyticsConsent), so calling both preserves the LL-0024 guarantee that no
 * event is emitted pre-consent. This wrapper deliberately adds no gating of its
 * own — one place to change consent behaviour, not two.
 *
 * GA4 is kept rather than replaced: it is what the existing reporting reads.
 */
export function captureConversion(
  name: string,
  params?: Record<string, string | number | boolean>,
): void {
  // ISOLATED PER SINK, and the two words carry different weight.
  //
  // Isolated, because every call site sits inside a form-submit `try` whose
  // `catch` sets the visible error state, and posthog-js `capture()` has no
  // internal try/catch (verified against posthog-js 1.404.1: capture() runs
  // property calculation, campaign params, the rate limiter and _send_request,
  // all unguarded). An SDK throw would therefore land in that catch and show
  // "something went wrong" to someone whose signup had already SUCCEEDED —
  // the server call was awaited and returned ok several lines earlier.
  //
  // Per sink, not around both, because a try/catch WRAPPING the pair was an
  // earlier defect in this very function: a GA4 throw skipped the PostHog call
  // entirely and silently emptied the funnel this exists to fill. Both sinks
  // are always attempted; neither can suppress the other.
  //
  // And reported, never swallowed. An analytics sink that fails invisibly is
  // exactly how a funnel dies without anyone noticing — which is the failure
  // mode this whole file is a response to.
  //
  // conversionParity.test.ts asserts these properties BEHAVIOURALLY (both sinks
  // receive identical payloads; either sink throwing still leaves the other
  // called; the two consent gates stay independent; nothing propagates to the
  // caller). Source-scanning this function was tried three times and defeated
  // three times, each by moving the defect one level down — a scan cannot see
  // what a callee actually does.
  fireSink("ga4", name, () => captureGa4Event(name, params));
  fireSink("posthog", name, () => captureEvent(name, params));
}

function fireSink(sink: "ga4" | "posthog", name: string, emit: () => void) {
  try {
    emit();
  } catch (err) {
    // Sentry FIRST, console second. A review found the earlier version of this
    // comment claiming failures were "reported, never swallowed" while it only
    // called console.error — and lib/sentry.ts initialises @sentry/react without
    // the CaptureConsole integration, so a console.error here reached exactly one
    // person's devtools and no monitoring anywhere. That is a swallow with a
    // reassuring comment on it, which is worse than an honest swallow.
    //
    // captureException no-ops safely when Sentry is uninitialised (no DSN), so
    // this is inert in tests and in any build without the env var.
    Sentry.captureException(err, {
      tags: { area: "analytics", sink },
      extra: { conversion: name },
    });
    console.error(
      `[analytics] ${sink} sink threw while capturing conversion "${name}"`,
      err,
    );
  }
}

/**
 * Reads UTM params from the current URL and registers them as super-properties
 * on PostHog so every subsequent event carries attribution context.
 * Safe to call on every page load — no-ops if PostHog isn't loaded or URL has no UTMs.
 */
export function captureUtm(): void {
  if (typeof window === "undefined" || !posthogLoaded) return;
  const params = new URLSearchParams(window.location.search);
  const utm: Record<string, string> = {};
  for (const key of [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
  ]) {
    const val = params.get(key);
    if (val) utm[key] = val;
  }
  if (Object.keys(utm).length > 0) {
    posthog.register(utm);
  }
}

/**
 * No-op. GA4 is loaded via the inline snippet in `index.html` (LL-0024).
 * Kept for backwards compatibility with any call sites that may import it.
 */
export function loadGa4(_id: string): void {
  // intentional no-op — see index.html <head> gtag snippet
}

export function loadMetaPixel(id: string): void {
  if (typeof window === "undefined" || !id) return;
  if (window.fbq) {
    window.fbq("track", "PageView");
    return;
  }
  const n: FbqFn = function (...args: unknown[]) {
    if (n.callMethod) {
      n.callMethod.apply(n, args);
    } else {
      (n.queue = n.queue || []).push(args);
    }
  } as FbqFn;
  n.push = n;
  n.loaded = true;
  n.version = "2.0";
  n.queue = [];
  window.fbq = n;
  window._fbq = window._fbq || n;
  const t = document.createElement("script");
  t.async = true;
  t.src = "https://connect.facebook.net/en_US/fbevents.js";
  const s = document.getElementsByTagName("script")[0];
  s.parentNode?.insertBefore(t, s);
  n("init", id);
  n("track", "PageView");
}
